import React, { useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileDropdown from './ProfileDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import AutoSaveBadge from './AutoSaveBadge';
import { NotificationBellButton } from './NotificationBellButton';
import SessionWarningDialog from './SessionWarningDialog';
import { CompanionDock } from './Companion';
import { CourseOutlineDrawer } from './LearningShell';
import { useCompanionStore } from '../store/companionStore';
import { useViewModeBootstrap } from './ViewMode/ViewModeShell';
import { OfflineBanner } from './StateVariants/ChromeStates';
import {
  confirmWorkspaceNavigation,
  hasWorkspaceNavigationGuard,
} from '../shell/workspaceNavigationGuard';
import styles from './Layout.module.css';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isIDEPage =
    location.pathname.startsWith('/lab/') ||
    location.pathname.startsWith('/environment/');
  const { t } = useTranslation();
  // Sync viewMode with the server-side preference once per SPA load. We
  // don't gate render on `ready` — the local store already has a sensible
  // default from localStorage, so learners never see a spinner while the
  // preferences call resolves.
  useViewModeBootstrap();
  // When Ako is docked open, reserve a right gutter on wide screens so the
  // page content sits beside the panel rather than under it (the panel itself
  // is a fixed overlay). On narrow screens the panel overlays and the student
  // collapses it.
  const akoOpen = useCompanionStore((s) => s.isOpen);
  const guardLinkNavigation = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        !hasWorkspaceNavigationGuard()
        || event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey
      ) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>('a[href]');
      if (
        !anchor
        || anchor.target === '_blank'
        || anchor.hasAttribute('download')
      ) return;
      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();
      void confirmWorkspaceNavigation().then((mayLeave) => {
        if (mayLeave) {
          navigate(`${destination.pathname}${destination.search}${destination.hash}`);
        }
      });
    },
    [navigate],
  );

  return (
    <div className={styles.container} onClickCapture={guardLinkNavigation}>
      <OfflineBanner />
      <SessionWarningDialog />
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/" className={styles.logo}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#6366f1"/>
              <path d="M2 17L12 22L22 17" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{t('header.brand_name')}</span>
          </Link>
        </div>

        <nav className={styles.nav}>
          <Link
            to="/dashboard"
            className={`${styles.navLink} ${location.pathname === '/dashboard' ? styles.navLinkActive : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            {t('nav.dashboard')}
          </Link>
          <Link
            to="/labs"
            className={`${styles.navLink} ${location.pathname === '/labs' ? styles.navLinkActive : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 6L8 2L14 6V10L8 14L2 10V6Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 6V14" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 6L8 10L14 6" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            {t('nav.labs')}
          </Link>
          {/* "My Learning" folds in the old My Labs + Progress destinations —
              both showed the learner's started labs, so they're one place now.
              Stays active on the legacy /progress path (which now redirects). */}
          <Link
            to="/my-labs"
            className={`${styles.navLink} ${location.pathname === '/my-labs' || location.pathname === '/progress' ? styles.navLinkActive : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4.5C2 3.67157 2.67157 3 3.5 3H12.5C13.3284 3 14 3.67157 14 4.5V11.5C14 12.3284 13.3284 13 12.5 13H3.5C2.67157 13 2 12.3284 2 11.5V4.5Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M5 7L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('nav.my_labs')}
          </Link>
        </nav>

        <div className={styles.headerRight}>
          {isIDEPage && <AutoSaveBadge />}
          <NotificationBellButton />
          <LanguageSwitcher />
          <ProfileDropdown />
        </div>
      </header>
      <main className={`${styles.main} ${akoOpen && !isIDEPage ? styles.mainDockOpen : ''}`}>
        <Outlet />
      </main>
      {/* Learning Shell rails — left = course outline ("where am I going"),
          right = Companion tutor ("who's helping me"). Both collapsible,
          present on every screen. See docs/ide-ux-spec.md §16.1. */}
      <CourseOutlineDrawer />
      <CompanionDock />
    </div>
  );
};

export default Layout;
